import { hydrateSceneryAirport, resolveCoords } from "./airports";
import { haversineNm } from "./geo";
import { jetRejectsCharterStage } from "./charterRealism";
import type { AircraftSpec, CharterRequest, Mission, SceneryAirport } from "./types";

/** Paying reverse fill vs a normal outbound rate card. Empty ferry stays $0. */
export const BACKHAUL_QUOTE_FACTOR = 0.62;

/** How often an outbound that ends away also offers a cheaper reverse RFQ. */
export const BACKHAUL_OFFER_RATE = 0.38;

/** Occasional two-party / drop-one itinerary (first slice: two paying legs). */
export const MULTI_STOP_OFFER_RATE = 0.12;

/** Turn after the outbound before an empty return or backhaul STD. */
export const RETURN_TURN_MIN = 40;

export type CharterItineraryKind = "one_way" | "round_trip_empty" | "backhaul" | "multi_stop" | "tech_stop";

export type CharterProposedLeg = {
  originIcao: string;
  destIcao: string;
  originName?: string;
  destName?: string;
  paxCount: number;
  /** Deadhead / empty ferry — not revenue. */
  empty?: boolean;
  /** Refuel / range stop — pax stay aboard; not a pax revenue sector. */
  techStop?: boolean;
  notes?: string;
};

export const ITINERARY_KIND_LABEL: Record<CharterItineraryKind, string> = {
  one_way: "One-way",
  round_trip_empty: "Outbound + empty return",
  backhaul: "Backhaul fill",
  multi_stop: "Multi-stop",
  tech_stop: "Tech stop",
};

export function hydrateItineraryKind(raw: unknown): CharterItineraryKind | undefined {
  if (
    raw === "one_way" ||
    raw === "round_trip_empty" ||
    raw === "backhaul" ||
    raw === "multi_stop" ||
    raw === "tech_stop"
  ) {
    return raw;
  }
  return undefined;
}

export function icaoKey(value: string | undefined): string {
  return (value ?? "").trim().toUpperCase();
}

/** Aircraft ends away from home — outbound implies a deadhead home unless a fill rides. */
export function needsEmptyReturn(
  destIcao: string,
  homeIcao?: string,
  opts?: { mxPositioning?: boolean; itineraryKind?: CharterItineraryKind },
): boolean {
  if (opts?.mxPositioning) return false;
  if (opts?.itineraryKind === "backhaul") return false;
  const dest = icaoKey(destIcao);
  const home = icaoKey(homeIcao);
  if (!dest || !home) return false;
  return dest !== home;
}

export function returnEmptyDestIcao(homeIcao?: string, originIcao?: string): string {
  const home = icaoKey(homeIcao);
  if (home) return home;
  return icaoKey(originIcao);
}

export function shouldOfferBackhaul(rng: () => number): boolean {
  return rng() < BACKHAUL_OFFER_RATE;
}

export function shouldOfferMultiStop(rng: () => number): boolean {
  return rng() < MULTI_STOP_OFFER_RATE;
}

export function applyQuoteDiscount(
  quote: { revenueCad: number },
  factor: number | undefined,
): number {
  const f = typeof factor === "number" && Number.isFinite(factor) ? Math.min(1, Math.max(0.2, factor)) : 1;
  return Math.round(quote.revenueCad * f * 100) / 100;
}

export function returnEmptyDeparture(charterDep: Date, eteMin: number, turnMin = RETURN_TURN_MIN): Date {
  const ete = Number.isFinite(eteMin) ? Math.max(0, eteMin) : 0;
  return new Date(charterDep.getTime() + (ete + turnMin) * 60_000);
}

export function backhaulDepartureIso(outboundDep: Date, outboundEteMin: number, turnMin = RETURN_TURN_MIN): string {
  return returnEmptyDeparture(outboundDep, outboundEteMin, turnMin).toISOString();
}

export function payingProposedLegs(
  legs: ReadonlyArray<CharterProposedLeg> | undefined,
): CharterProposedLeg[] {
  return (legs ?? []).filter((leg) => !leg.empty);
}

export function itineraryRouteLabel(
  row: Pick<CharterRequest, "originIcao" | "destIcao" | "proposedLegs" | "itineraryKind" | "returnEmpty">,
): string {
  const paying = payingProposedLegs(row.proposedLegs);
  if (paying.length > 1) {
    const first = paying[0]!;
    return [first.originIcao, ...paying.map((l) => l.destIcao)].join("–");
  }
  const base = `${row.originIcao}–${row.destIcao}`;
  if (row.itineraryKind === "backhaul") return `${base} · backhaul`;
  if (row.returnEmpty || row.itineraryKind === "round_trip_empty") {
    return `${base} · empty return`;
  }
  return base;
}

export function itineraryCaption(
  row: Pick<
    CharterRequest,
    "itineraryKind" | "returnEmpty" | "quoteDiscount" | "destIcao" | "originIcao" | "proposedLegs"
  >,
): string | null {
  const kind = row.itineraryKind;
  if (kind === "backhaul") {
    const pct = row.quoteDiscount != null ? Math.round((1 - row.quoteDiscount) * 100) : Math.round((1 - BACKHAUL_QUOTE_FACTOR) * 100);
    return `Cheaper reverse fill — ${pct}% off a normal outbound. Taken backhaul is charter revenue.`;
  }
  if (kind === "multi_stop") {
    return `Multi-stop: drop one party / carry two. Paying legs ${itineraryRouteLabel(row)}.`;
  }
  if (kind === "tech_stop") {
    return `Tech / refuel stop — not a pax revenue sector. Route ${itineraryRouteLabel(row)}.`;
  }
  if (row.returnEmpty || kind === "round_trip_empty") {
    return `Outbound ends at ${row.destIcao}. Empty return ferry is not revenue unless a backhaul fill rides.`;
  }
  return null;
}

export function pickViaAirport(input: {
  origin: SceneryAirport;
  dest: SceneryAirport;
  scenery: SceneryAirport[];
  spec: AircraftSpec;
  paxCount: number;
  rng: () => number;
}): SceneryAirport | null {
  const originIcao = icaoKey(input.origin.icao);
  const destIcao = icaoKey(input.dest.icao);
  const oc = resolveCoords(originIcao, [input.origin, ...input.scenery]);
  const dc = resolveCoords(destIcao, [input.dest, ...input.scenery]);
  if (!oc || !dc) return null;
  const direct = haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);
  const candidates = input.scenery
    .map((raw) => hydrateSceneryAirport(raw.icao, input.scenery))
    .filter((via): via is SceneryAirport => {
      if (!via) return false;
      const icao = icaoKey(via.icao);
      if (icao === originIcao || icao === destIcao) return false;
      const vc = resolveCoords(icao, [via, ...input.scenery]);
      if (!vc) return false;
      const a = haversineNm(oc.lat, oc.lon, vc.lat, vc.lon);
      const b = haversineNm(vc.lat, vc.lon, dc.lat, dc.lon);
      if (a < 40 || b < 40) return false;
      if (a + b > direct * 1.85) return false;
      if (jetRejectsCharterStage(input.spec, originIcao, icao, a)) return false;
      if (jetRejectsCharterStage(input.spec, icao, destIcao, b)) return false;
      return true;
    })
    .sort(() => input.rng() - 0.5);
  return candidates[0] ?? null;
}

export function planOutboundItinerary(input: {
  origin: SceneryAirport;
  dest: SceneryAirport;
  spec: AircraftSpec;
  paxCount: number;
  homeIcao?: string;
  scenery: SceneryAirport[];
  rng: () => number;
  mxPositioning?: boolean;
  /**
   * When false, skip the occasional generated-inquiry multi-stop roll.
   * Omitted keeps that roll (idle / Grok decorate). Desk Create passes false:
   * the typed city-pair is the paying leg. Tech stops and empty return are unchanged.
   */
  allowMultiStop?: boolean;
}): {
  kind: CharterItineraryKind;
  returnEmpty: boolean;
  proposedLegs: CharterProposedLeg[];
} {
  const home = icaoKey(input.homeIcao) || "CYPD";
  const empty = needsEmptyReturn(input.dest.icao, home, { mxPositioning: input.mxPositioning });
  const offerMultiStop = input.allowMultiStop !== false && shouldOfferMultiStop(input.rng);
  if (!input.mxPositioning && offerMultiStop) {
    const via = pickViaAirport({
      origin: input.origin,
      dest: input.dest,
      scenery: input.scenery,
      spec: input.spec,
      paxCount: input.paxCount,
      rng: input.rng,
    });
    if (via) {
      const drop = Math.max(1, Math.min(input.paxCount - 1, 2));
      const remaining = Math.max(1, input.paxCount - drop);
      const legs: CharterProposedLeg[] = [
        {
          originIcao: input.origin.icao,
          destIcao: via.icao,
          originName: input.origin.name,
          destName: via.name,
          paxCount: input.paxCount,
          notes: `Party of ${input.paxCount}; drop ${drop} at ${via.icao}.`,
        },
        {
          originIcao: via.icao,
          destIcao: input.dest.icao,
          originName: via.name,
          destName: input.dest.name,
          paxCount: remaining,
          notes: `${remaining} continue ${via.icao}–${input.dest.icao}.`,
        },
      ];
      if (empty) {
        legs.push({
          originIcao: input.dest.icao,
          destIcao: home,
          destName: "Home",
          paxCount: 0,
          empty: true,
          notes: "Empty return ferry — no charter revenue.",
        });
      }
      return { kind: "multi_stop", returnEmpty: empty, proposedLegs: legs };
    }
  }

  const legs: CharterProposedLeg[] = [
    {
      originIcao: input.origin.icao,
      destIcao: input.dest.icao,
      originName: input.origin.name,
      destName: input.dest.name,
      paxCount: input.paxCount,
    },
  ];
  if (empty) {
    legs.push({
      originIcao: input.dest.icao,
      destIcao: home,
      paxCount: 0,
      empty: true,
      notes: "Empty return ferry — no charter revenue.",
    });
  }
  return {
    kind: empty ? "round_trip_empty" : "one_way",
    returnEmpty: empty,
    proposedLegs: legs,
  };
}

export function shouldFileReturnEmpty(input: {
  request: Pick<CharterRequest, "returnEmpty" | "itineraryKind" | "destIcao" | "mxPositioning" | "linkedRequestId">;
  inbox: ReadonlyArray<Pick<CharterRequest, "id" | "status" | "itineraryKind" | "linkedRequestId">>;
  homeIcao?: string;
}): boolean {
  if (!needsEmptyReturn(input.request.destIcao, input.homeIcao, {
    mxPositioning: input.request.mxPositioning,
    itineraryKind: input.request.itineraryKind,
  })) {
    return false;
  }
  if (
    !input.request.returnEmpty &&
    input.request.itineraryKind !== "round_trip_empty" &&
    input.request.itineraryKind !== "multi_stop" &&
    input.request.itineraryKind !== "tech_stop"
  ) {
    return false;
  }
  const linked = input.request.linkedRequestId;
  if (linked) {
    const fill = input.inbox.find(
      (row) =>
        row.id === linked &&
        row.itineraryKind === "backhaul" &&
        (row.status === "accepted" || row.status === "assigned"),
    );
    if (fill) return false;
  }
  return true;
}

export function findPendingReturnFerry(
  missions: ReadonlyArray<Pick<Mission, "id" | "flightType" | "status" | "inboxRequestId" | "originIcao" | "destIcao">>,
  outboundRequestId: string | undefined,
  backhaul: Pick<CharterRequest, "originIcao" | "destIcao">,
): { id: string } | null {
  if (!outboundRequestId) return null;
  const hit = missions.find(
    (m) =>
      m.inboxRequestId === outboundRequestId &&
      m.flightType === "ferry" &&
      (m.status === "pending" || m.status === "in-progress") &&
      icaoKey(m.originIcao) === icaoKey(backhaul.originIcao) &&
      icaoKey(m.destIcao) === icaoKey(backhaul.destIcao),
  );
  return hit ? { id: hit.id } : null;
}

export function itineraryNoteForEmail(plan: { kind: CharterItineraryKind; returnEmpty: boolean; proposedLegs: CharterProposedLeg[] }): string {
  if (plan.kind === "tech_stop") {
    const via = plan.proposedLegs.find((l) => l.techStop)?.destIcao;
    return via && via !== "TBD"
      ? `Tech / refuel stop at ${via}. That hop is not a pax revenue sector.`
      : "Tech stop required — pick an addon or manual ICAO. That hop is not pax revenue.";
  }
  if (plan.kind === "multi_stop") {
    const paying = payingProposedLegs(plan.proposedLegs);
    const via = paying[0]?.destIcao;
    return via
      ? `Two parties / drop at ${via}: ${paying.map((l) => `${l.originIcao}–${l.destIcao} (${l.paxCount} pax)`).join("; ")}.`
      : "Multi-stop charter — drop one party, carry the rest.";
  }
  if (plan.returnEmpty || plan.kind === "round_trip_empty") {
    return "Aircraft will return empty unless a reverse / backhaul fill is booked. Empty ferry is not charter revenue.";
  }
  return "";
}

export function backhaulEmailNote(): string {
  return "Backhaul / empty-leg fill — discounted vs a normal outbound. If taken this is paying charter, not a company ferry.";
}
